import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppTitleService } from '../../services/app-title.service';
import { AsyncPipe, Location } from '@angular/common';
import { Documento } from '../../models/Document';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DocumentoService } from '../../services/documento.service';
import { DateService, MY_DATE_FORMATS } from '../../shared/date.service';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { map, Observable, startWith } from 'rxjs';
import { ParticipanteService } from '../../services/participante.service';
import { UnidadOrganizacionalService } from '../../services/unidad-organizacional.service';
import { UnidadOrganizacional } from '../../models/UnidadOrganizacional';
import { ServidorPublico } from '../../models/ServidorPublico';
import { AuthService } from '../../services/auth.service';
import { ScnackbarData, ToastService, SnackbarStyles } from '../toast.service';
import { AccionTarea } from '../../models/Tarea';
import { TareaService } from '../../services/tarea.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { UltimoUsoPipe } from '../../pipes/ultimo-uso.pipe';
import { UtilService } from '../../services/util.service';
import { TableSelectedDocumentsComponent } from "../table-selected-documents/table-selected-documents.component";
import { TreeAutocompleteComponent } from '../tree-autocomplete/tree-autocomplete.component';

export function participanteValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const participante: string | ServidorPublico = control.value;
    if ( participante && !(participante as ServidorPublico).id) {
      return { required: true }
    }
    return null
  };
}

export function departamentoValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const departamento: string | UnidadOrganizacional = control.value;
    if (departamento && !(departamento as UnidadOrganizacional).id) {
      return { required: true }
    }
    return null
  };
}

@Component({
    selector: 'app-reasignar',
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'es-EC' },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    ],
    imports: [
        MatTableModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatDatepickerModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        AsyncPipe,
        TreeAutocompleteComponent,
        MatIconModule,
        UltimoUsoPipe,
        MatProgressSpinnerModule,
        TableSelectedDocumentsComponent
    ],
    templateUrl: './reasignar.component.html',
    styleUrl: './reasignar.component.scss'
})
export class ReasignarComponent implements OnInit {
  documentIDs: number[]
  currentUserID: string
  utilService = inject(UtilService)
  token = this.authService.getTokenData()

  documentsInformation: Documento[] = []

  reasignarFormGroup = new FormGroup({
    participante: new FormControl<ServidorPublico>({ id: 0, usuario: "", apellidos: "", nombres: "", correo: "", estado: 0, telefono: "", telefonoMovil: "", departamento: "", cargo: "", titulo: "", ciruc: "", fechaUso: "" }, {
      validators: [Validators.required, participanteValidator()]
    }),
    maxDate: new FormControl(new Date(), {
      validators: [Validators.required]
    }),
    comment: new FormControl("", {
      validators: [
        Validators.required,
        Validators.maxLength(255)
      ]
    })
  })

  participantes: ServidorPublico[] = []
  participantesFiltered?: Observable<ServidorPublico[]>

  departamentos: UnidadOrganizacional[] = []
  departamentosFiltered?: Observable<UnidadOrganizacional[]>

  fetchingReasignar = false
  snackbarData: ScnackbarData = {}
  minDate = new Date()

  dsDepartamentos: UnidadOrganizacional[] = []

  constructor(
    private router: Router,
    private dateService: DateService,
    documentoService: DocumentoService,
    private location: Location,
    private participanteService: ParticipanteService,
    private unidadOrganizacionalService: UnidadOrganizacionalService,
    private tareaService: TareaService,
    private authService: AuthService,
    private toast: ToastService,
  ) {
    inject(AppTitleService).setTitle("Reasignar")

    const navigation = this.router.getCurrentNavigation();
    if (!navigation) {
      location.back()
    }

    this.documentIDs = navigation?.extras.state?.["documentIDs"]

    this.documentIDs.forEach((id) => {
      documentoService.getById(id).subscribe({
        next: (response) => {
          this.documentsInformation = [...this.documentsInformation, response]
        }
      })
    })

    this.currentUserID = this.token!.id
  }

  ngOnInit(): void {
    this.reasignarFormGroup.controls.participante.disable()
    this.unidadOrganizacionalService.getDepartamentosTree().subscribe({
      next: (response) => {
        this.dsDepartamentos = response
      }
    })
  }

  onReasignarSubmit() {
    this.fetchingReasignar = true
    let accionTareas: AccionTarea[] = []
    const participante = this.reasignarFormGroup.controls.participante.value
    const maxDate = this.reasignarFormGroup.controls.maxDate.value
    const comment = this.reasignarFormGroup.controls.comment.value
    this.documentsInformation.forEach(document => {
      const accionTarea: AccionTarea = {
        id: document.idTarea,
        idParticipante: participante!.id,
        fechaExpira: this.dateService.formatDateToCustomStringNoTime(maxDate!),
        comentario: comment!,
        usuarioResponsable: this.token?.sub,
      }
      accionTareas.push(accionTarea)
    });

    this.tareaService.ReasignarDocumento(accionTareas).subscribe({
      next: (_) => {
        this.snackbarData.message = "Documento reasignado!"
        this.snackbarData.style = SnackbarStyles.Success
        this.snackbarData.iconName = "check_circle"
        this.fetchingReasignar = false
        this.toast.show(this.snackbarData)
        this.utilService.callTasks();
        this.router.navigateByUrl("/enviado")
      },
      error: (_) => {
        this.snackbarData.message = "Error reasignando documento!"
        this.snackbarData.style = SnackbarStyles.Error
        this.snackbarData.iconName = "error"
        this.fetchingReasignar = false
        this.toast.show(this.snackbarData)
      }
    })
  }

  trackDepartamentoChanges(value: any) {
      if ((value as UnidadOrganizacional).id) {
        this.participanteService.getServidoresPorDepartamento((value as UnidadOrganizacional).id).subscribe({
          next: (response) => {
            response = response.filter(participante => participante.id.toString() !== this.currentUserID);
            if (response.length > 0) {
              response.sort(function (a, b) {
                if (a.apellidos > b.apellidos) { return 1; }
                if (a.apellidos < b.apellidos) { return -1; }
                return 0;
              });
              this.participantes = response
              this.participantesFiltered = this.reasignarFormGroup.controls.participante.valueChanges.pipe(
                startWith(''),
                map(value => {
                  const nombres = typeof value === 'string' ? value : value?.nombres;
                  return nombres ? this._filterParticipante(nombres as string) : this.participantes.slice();
                }),
              );
              this.reasignarFormGroup.controls.participante.enable()
            } else {
              this.snackbarData.message = "Sin usuarios en el departamento!"
              this.snackbarData.style = SnackbarStyles.Error
              this.snackbarData.iconName = "error"
              this.toast.show(this.snackbarData)
            }
          }
        })
      } else {
        this.participantes = []
        this.participantesFiltered = undefined
        this.reasignarFormGroup.controls.participante.setValue(null)
      }
  }

  displayParticipanteFn(participante: ServidorPublico): string {
    return participante && participante.nombres ? participante.nombres.trim() + ' ' + participante.apellidos.trim() : '';
  }

  private _filterParticipante(filter: string): ServidorPublico[] {
    const filterValue = filter.toLowerCase();

    return this.participantes.filter(participante =>
      participante.nombres.toLowerCase().includes(filterValue) ||
      participante.apellidos.toLocaleLowerCase().includes(filterValue) ||
      participante.cargo?.toLowerCase().includes(filterValue) ||
      participante.ciruc.includes(filterValue));
  }


  displayDepartamentoFn(departamento: UnidadOrganizacional): string {
    return departamento && departamento.nombre ? departamento.nombre : '';
  }

  private _filterDepartamento(filter: string): UnidadOrganizacional[] {
    const filterValue = filter.toLowerCase();

    return this.departamentos.filter(departamento =>
      departamento.nombre.toLowerCase().includes(filterValue));
  }

  selectAllText(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  onCancel() {
    this.location.back()
  }


  formatDate(date: string): string {
    return this.dateService.formatCustomStringToDate(date)
  }
}
